import { API, useDevices } from '@unboared/react';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { LuQrCode, LuX } from 'react-icons/lu';
import { Button } from '~/components/ui/button';
import { useGlobalPopup } from '~/components/design/popup/GlobalPopup';
import { isAirportClient } from '~/config/airports.config';
import { useFreePlayState } from '~/hooks/freePlay';
import { useSyncClient } from '~/hooks/models/useClient';
import { Mode, ClientDatabase } from '~/types/client';
import QRCodeView from './QRCodeView2';
import QRCodeViewORY from './QRCodeViewORY';
import QRCodeViewBOD from './QRCodeViewBOD';

export function QRCodeButton({ onToggle }: { onToggle?: (open: boolean) => void } = {}) {
    const { clientID } = useParams();
    const { gamepadIDs } = useDevices();
    const freePlay = useFreePlayState();
    // Auto-show the big QR overlay on airport screens with no gamepad connected.
    // Free-play clients are excluded: their idle state is the FreePlayIdleScreen,
    // which embeds its own session QR — the popup would just cover that attract screen.
    const showBigQRCodeAuto = isAirportClient(clientID) && !freePlay.enabled && gamepadIDs.length === 0;

    // Animator Control : l'animateur déclenche le QR géant à distance depuis son
    // téléphone (/animator) → flag Firestore screenInfos.anim.showQR, lu en live.
    const client = useSyncClient(clientID || '');
    const showBigQRCodeAnimator = client?.getScreen(Mode.Anim)?.showQR ?? false;
    const showBigQRCode = showBigQRCodeAuto || showBigQRCodeAnimator;

    // Ref vers la dernière valeur du flag animateur, lue dans le callback de
    // fermeture (closure sinon périmée).
    const animFlagRef = useRef(showBigQRCodeAnimator);
    animFlagRef.current = showBigQRCodeAnimator;

    const { isOpen, open, close } = useGlobalPopup();

    const pickAirportView = () => {
        if (clientID === 'airport-BOD') return <QRCodeViewBOD />;
        if (clientID === 'airport-ORY') return <QRCodeViewORY />;
        return <QRCodeView />;
    };

    const handleClose = () => {
        API.resume();
        // Resync : si le QR est fermé sur la TV (bouton local) alors que le flag
        // animateur est encore actif, on le remet à false pour que le cockpit
        // reflète la réalité (sinon il croit le QR affiché → double appui).
        // Pas d'écriture quand le flag est déjà false (fermeture pilotée par
        // l'animateur, ou auto-aéroport) → pas de write parasite.
        if (clientID && animFlagRef.current) {
            // Log si l'écriture échoue : un échec silencieux laisserait le cockpit
            // croire le QR encore affiché (= le 'double appui' que ce code corrige).
            ClientDatabase.setScreenShowQR(clientID, Mode.Anim, false).catch((e) =>
                console.error('[animator] resync showQR=false failed', e)
            );
        }
    };

    const openBigQRCode = () => {
        if (!isOpen()) {
            API.pause();
            open(pickAirportView(), handleClose);
        } else {
            close();
        }
    };

    useEffect(() => {
        if (showBigQRCode) {
            openBigQRCode();
        }
        if (!showBigQRCode && isOpen()) {
            close();
        }
    }, [showBigQRCode]);

    return (
        <Button
            preset="secondary"
            icon={isOpen() ? <LuX /> : <LuQrCode />}
            onClick={() => {
                onToggle?.(!isOpen());
                openBigQRCode();
            }}
        />
    );
}
